// Общите парчета за прегледа на последствията в потвърждаващия диалог:
// „продукт — количество мярка" по правилата на модула (количества със
// запетая и толкова знака, колкото има мерната единица; знак „−" за липса).
import { useMemo } from "react";
import type { TFunction } from "i18next";
import type { Product } from "../../../../api/inventory";
import { useProducts } from "../../../../hooks/useInventory";
import { formatQty } from "../qty";
import type { ConfirmNote, ConfirmPreviewItem } from "./confirmTypes";

// „2,000 кг" · със `signed` → „+2,000 кг" / „−1,000 кг"
export const formatQtyUnit = (
  quantity: number,
  decimals: number | null | undefined,
  unit?: string | null,
  signed = false,
): string => {
  const sign = signed ? (quantity < 0 ? "−" : quantity > 0 ? "+" : "") : quantity < 0 ? "−" : "";
  const text = `${sign}${formatQty(Math.abs(quantity), decimals)}`;
  return unit?.trim() ? `${text} ${unit.trim()}` : text;
};

export type PreviewProductLine = {
  key?: string | number;
  productId: number;
  productName?: string | null;
  quantity: number;
  unit?: string | null;
};

// Редове на документ/лист → редове за прегледа (името и мярката — от реда,
// иначе от справочника на продуктите)
export const productLineItems = (
  lines: PreviewProductLine[],
  productById: Map<number, Product>,
  options: { signed?: boolean; tone?: ConfirmPreviewItem["tone"] } = {},
): ConfirmPreviewItem[] =>
  lines.map((line, index) => {
    const product = productById.get(line.productId);
    return {
      key: line.key ?? `${line.productId}-${index}`,
      label: line.productName || product?.name || `#${line.productId}`,
      value: formatQtyUnit(line.quantity, product?.decimalPlaces, line.unit ?? product?.unit, options.signed),
      tone: options.tone,
    };
  });

// Обобщение на чернова „Домати — 2,000 кг" → ред за прегледа (име вляво,
// количество вдясно)
export const summaryLineItem = (line: string, index: number): ConfirmPreviewItem => {
  const split = line.lastIndexOf(" — ");
  return split === -1
    ? { key: index, label: line }
    : { key: index, label: line.slice(0, split), value: line.slice(split + 3) };
};

// Бележката под анулирането: документът не изчезва — връща се от справката
// „Анулирани дейности" (бутон „Възстанови")
export const annulUndoNote = (t: TFunction): ConfirmNote => ({
  kind: "undo",
  text: t("inventoryPage.cfmAnnulUndo", { report: t("inventoryPage.deletedReport") }),
});

// Справочникът на продуктите по id — за викащите, които нямат своя map
export const useProductById = (): Map<number, Product> => {
  const { data: products } = useProducts();
  return useMemo(() => new Map((products ?? []).map((product) => [product.id, product])), [products]);
};
